import { Component, Inject, OnInit } from '@angular/core';
import { AuthEventHandlerService, AuthService, CookiesService } from '@jissu-public/shared/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IEnvironment } from '@jissu-public/jissu-public-dm-dc/shared/model';
import { filter, takeUntil, tap } from 'rxjs';
import { SafeUnsubscribeComponent } from '@jissu-public/shared/ui-lib';
import { environment } from '../environments/environment';
import { Title } from '@angular/platform-browser';
import { SUPPORTED_LANGUAGES } from '@jissu-public/shared/model';

// eslint-disable-next-line @typescript-eslint/ban-types,@typescript-eslint/naming-convention
declare const gtag: Function;

const LANGUAGE_STORAGE_KEY = 'jissu-public-dm-dc.lang';

@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>',
})
export class AppComponent extends SafeUnsubscribeComponent implements OnInit {
  title = 'jissu-public-dm-dc-ui';
  private readonly defaultTitle = 'JISŠÚ DM';
  private currentTitleKey = '';
  /**
   * Jazyk z URL, s ktorym sa aplikacia nacitala. Citame ho v konstruktore, teda predtym,
   * nez prvu navigaciu prepise `JissuPathLocationStrategy` svojim defaultom `lang=sk`.
   */
  private readonly initialUrlLanguage: string | undefined;

  constructor(
    private authEventHandlerService: AuthEventHandlerService,
    public translate: TranslateService,
    protected authService: AuthService,
    protected router: Router,
    private cookiesService: CookiesService,
    private titleService: Title,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    @Inject('ENVIRONMENT') private environment: IEnvironment
  ) {
    super();

    this.initialUrlLanguage = this.resolveUrlLanguage();

    translate.addLangs(SUPPORTED_LANGUAGES);
    translate.setDefaultLang('sk');
    translate.use(this.resolveInitialLanguage());
    this.setDocumentLanguage(translate.currentLang);
    this.restoreLanguageInUrl();

    if (environment.googleAnalyticsId && environment.googleAnalyticsId != '') {
      (<any>window)['ga-disable-' + environment.googleAnalyticsId] =
        !this.cookiesService.cookiesSettings$().value.analytics;

      this.loadGoogleAnalyticsScript();

      this.cookiesService
        .cookiesSettings$()
        .pipe(
          takeUntil(this.unsubscribe$),
          tap((value) => {
            (<any>window)['ga-disable-' + environment.googleAnalyticsId] = !value.analytics;
          })
        )
        .subscribe();

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (this.cookiesService.cookiesSettings$().value.analytics) {
            gtag('config', environment.googleAnalyticsId, {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              page_location: window.location.href,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              page_path: event.urlAfterRedirects,
            });
          }
        }
      });
    }
  }

  ngOnInit() {
    this.authEventHandlerService.initEventHandler();
    this.authService.checkAuth().subscribe(() => this.restoreLanguageInUrl());
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.synchronizeLanguageWithUrl();
        this.currentTitleKey = this.getTitle(this.activatedRoute);
        this.updateTitle();
      });

    this.translate.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe((event: LangChangeEvent) => {
      this.storeLanguage(event.lang);
      this.setDocumentLanguage(event.lang);
      this.updateTitle();
    });
  }

  private loadGoogleAnalyticsScript() {
    const body = document.body;

    const googletagmanagerScript = document.createElement('script');
    googletagmanagerScript.async = true;
    googletagmanagerScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
    body.appendChild(googletagmanagerScript);

    const gtagScript = document.createElement('script');
    gtagScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${environment.googleAnalyticsId}');
    `;
    body.appendChild(gtagScript);
  }

  getTitle(route: ActivatedRoute): string {
    let titleKey = '';
    let childRoute = route;

    while (childRoute.firstChild) {
      childRoute = childRoute.firstChild;
    }

    if (childRoute.snapshot.data && childRoute.snapshot.data['title']) {
      titleKey = childRoute.snapshot.data['title'];
    }

    return titleKey;
  }

  private updateTitle() {
    if (this.currentTitleKey) {
      this.translateService.get(this.currentTitleKey).subscribe((translatedTitle: string) => {
        this.titleService.setTitle(`${translatedTitle} | STATdata`);
      });
    } else {
      this.titleService.setTitle(`${this.defaultTitle} | STATdata`);
    }
  }

  /**
   * WCAG 3.1.1 - jazyk dokumentu musi zodpovedat jazyku obsahu, inak citacka cita
   * anglicku verziu slovenskou vyslovnostou.
   */
  private setDocumentLanguage(language: string | undefined): void {
    if (language && typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }

  private resolveInitialLanguage(): string {
    return this.initialUrlLanguage ?? this.resolveStoredLanguage() ?? 'sk';
  }

  private resolveUrlLanguage(): string | undefined {
    const routerLang = this.router.parseUrl(this.router.url).queryParams['lang'];
    const browserLang =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('lang') : undefined;
    const lang = (routerLang || browserLang || '').toLowerCase();

    return lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : undefined;
  }

  private synchronizeLanguageWithUrl() {
    const language = this.resolveUrlLanguage() ?? this.translate.currentLang ?? 'sk';
    if (this.translate.currentLang !== language) {
      this.translate.use(language);
    }
  }

  /**
   * Prihlasenie aj odhlasenie vedie cez Keycloak a angular-auth-oidc-client po spracovani
   * callbacku vycisti query parametre z URL (historyCleanUp) — `lang` sa tym stratil a
   * `JissuPathLocationStrategy` dalsim navigaciam doplnila svoj default `lang=sk`.
   * Zvoleny jazyk preto drzime aj v localStorage a po navrate ho vratime do URL.
   * Jazyk z URL ma prednost, aby zdielany odkaz s `?lang=` stale rozhodoval.
   */
  private restoreLanguageInUrl(): void {
    if (this.initialUrlLanguage || typeof window === 'undefined') {
      return;
    }

    const language = this.resolveStoredLanguage();
    if (!language) {
      return;
    }

    if (this.translate.currentLang !== language) {
      this.translate.use(language);
    }

    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('lang', language);
    window.history.replaceState({}, '', `${window.location.pathname}?${queryParams.toString()}`);
  }

  private resolveStoredLanguage(): string | undefined {
    let stored: string | null = null;
    try {
      stored = typeof window === 'undefined' ? null : window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      // localStorage nemusi byt dostupny (privatny rezim prehliadaca) - jazyk vtedy drzi len URL
    }
    const language = stored?.toLowerCase();

    return language && SUPPORTED_LANGUAGES.includes(language) ? language : undefined;
  }

  private storeLanguage(language: string): void {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      }
    } catch {
      // localStorage nemusi byt dostupny (privatny rezim prehliadaca) - jazyk vtedy drzi len URL
    }
  }
}
